import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getSectionPerms, isSubItemVisible } from '../../permissions';
import {
  AlertCircle,
  CalendarDays,
  Check,
  Clock3,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEAVE_TYPES = ['Casual Leave', 'Sick Leave', 'Annual Leave', 'Unpaid Leave', 'Other'];

const indiaToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const emptyForm = () => ({
  user_id: '',
  from_date: indiaToday(),
  to_date: indiaToday(),
  leave_type: 'Casual Leave',
  reason: '',
  notes: '',
});

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatDate = value => value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '-';

const leavePeriod = leave => leave.from_date === leave.to_date
  ? formatDate(leave.from_date)
  : `${formatDate(leave.from_date)} – ${formatDate(leave.to_date)}`;

const leaveTiming = (leave, today) => {
  if (leave.from_date <= today && leave.to_date >= today) return 'current';
  if (leave.from_date > today) return 'upcoming';
  return 'past';
};

export const Leaves = () => {
  const canView = isSubItemVisible('hr', 'leaves');
  const permissions = getSectionPerms('leaves');
  const today = indiaToday();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [deleteLeave, setDeleteLeave] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [timingFilter, setTimingFilter] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [leaveResponse, employeeResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr/leaves`, authHeaders()),
        axios.get(`${BACKEND_URL}/api/hr/employees`, authHeaders()),
      ]);
      setLeaves(leaveResponse.data || []);
      setEmployees(employeeResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load leave records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [canView]);

  const openCreate = () => {
    if (!permissions.canAdd) return;
    setEditingLeave(null);
    setForm(emptyForm());
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const openEdit = leave => {
    if (!permissions.canEdit || leave.from_date < today) return;
    setEditingLeave(leave);
    setForm({
      user_id: leave.user_id,
      from_date: leave.from_date,
      to_date: leave.to_date,
      leave_type: leave.leave_type || 'Casual Leave',
      reason: leave.reason || '',
      notes: leave.notes || '',
    });
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const saveLeave = async event => {
    event.preventDefault();
    if (!form.user_id) { setError('Please select an employee'); return; }
    if (!form.leave_type.trim()) { setError('Leave type is required'); return; }
    if (!form.reason.trim()) { setError('Reason is required'); return; }
    if (form.from_date < today) { setError('Leave cannot be added or moved to a past date'); return; }
    if (form.to_date < form.from_date) { setError('To date cannot be before From date'); return; }

    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      if (editingLeave) {
        await axios.put(`${BACKEND_URL}/api/hr/leaves/${editingLeave.id}`, form, authHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/hr/leaves`, form, authHeaders());
      }
      setShowForm(false);
      setSuccess(editingLeave ? 'Leave updated successfully' : 'Leave added and attendance marked automatically');
      setEditingLeave(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save leave');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteLeave || !permissions.canDelete || deleteLeave.from_date < today) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.delete(`${BACKEND_URL}/api/hr/leaves/${deleteLeave.id}`, authHeaders());
      setDeleteLeave(null);
      setSuccess('Leave deleted and its automatic attendance entries removed');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete leave');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLeaves = useMemo(() => leaves.filter(leave => {
    const query = search.trim().toLowerCase();
    const matchesSearch = !query || [leave.user_name, leave.user_email, leave.leave_type, leave.reason]
      .some(value => value?.toLowerCase().includes(query));
    const timing = leaveTiming(leave, today);
    return matchesSearch && (timingFilter === 'all' || timing === timingFilter);
  }), [leaves, search, timingFilter, today]);

  const summary = useMemo(() => ({
    total: leaves.length,
    current: leaves.filter(leave => leaveTiming(leave, today) === 'current').length,
    upcoming: leaves.filter(leave => leaveTiming(leave, today) === 'upcoming').length,
    past: leaves.filter(leave => leaveTiming(leave, today) === 'past').length,
  }), [leaves, today]);

  if (!canView) return (
    <div className="rounded-sm border border-red-200 bg-red-50 p-6 text-red-700" data-testid="hr-access-denied">
      <div className="flex items-center gap-2 font-semibold"><AlertCircle className="h-5 w-5" /> Access denied</div>
      <p className="mt-1 text-sm">Your role does not have permission to view Leaves.</p>
    </div>
  );

  return (
    <div data-testid="leaves-page">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900">Leaves</h1>
          <p className="mt-1 text-slate-600">Schedule leave and automatically update attendance</p>
        </div>
        {permissions.canAdd && <button type="button" onClick={openCreate} data-testid="add-leave"
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-orange-500 px-6 py-3 font-bold uppercase tracking-wider text-white hover:bg-orange-600">
          <Plus className="h-5 w-5" /> Add Leave
        </button>}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ['Total Entries', summary.total, CalendarDays, 'bg-slate-100 text-slate-600'],
          ['Currently Away', summary.current, UserRound, 'bg-amber-100 text-amber-700'],
          ['Upcoming', summary.upcoming, Clock3, 'bg-blue-100 text-blue-700'],
          ['Completed', summary.past, Check, 'bg-green-100 text-green-700'],
        ].map(([label, value, Icon, tone]) => (
          <div key={label} className="rounded-sm border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 text-2xl font-bold text-slate-900">{value}</p></div>
              <span className={`rounded-full p-2.5 ${tone}`}><Icon className="h-5 w-5" /></span>
            </div>
          </div>
        ))}
      </div>

      {error && <div className="mb-4 flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-red-700"><AlertCircle className="h-5 w-5" />{error}<button type="button" onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button></div>}
      {success && <div className="mb-4 flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-4 py-3 text-green-700"><Check className="h-5 w-5" />{success}</div>}

      <div className="mb-6 flex flex-col gap-3 rounded-sm border border-slate-200 bg-white p-4 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={event => setSearch(event.target.value)}
            placeholder="Search employee, leave type, or reason..." data-testid="leave-search"
            className="w-full rounded-sm border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-orange-500 focus:outline-none" />
        </div>
        <select value={timingFilter} onChange={event => setTimingFilter(event.target.value)}
          className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none">
          <option value="all">All Leave</option>
          <option value="current">Currently Away</option>
          <option value="upcoming">Upcoming</option>
          <option value="past">Completed</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-sm border border-slate-200 bg-white">
        <div className="hidden grid-cols-[minmax(190px,1fr)_minmax(210px,1fr)_140px_1.5fr_100px] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 lg:grid">
          <div>Employee</div><div>Leave Period</div><div>Type</div><div>Reason</div><div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filteredLeaves.length === 0 ? (
          <div className="py-20 text-center">
            <CalendarDays className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="font-medium text-slate-700">No leave records found</p>
            <p className="mt-1 text-sm text-slate-500">Add leave in advance to mark the employee absent automatically.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredLeaves.map(leave => {
              const timing = leaveTiming(leave, today);
              return (
                <div key={leave.id} data-testid={`leave-row-${leave.id}`}
                  className="grid gap-3 px-5 py-4 lg:grid-cols-[minmax(190px,1fr)_minmax(210px,1fr)_140px_1.5fr_100px] lg:items-center lg:gap-4">
                  <div><p className="font-semibold text-slate-900">{leave.user_name || 'Unknown User'}</p><p className="text-xs text-slate-500">{leave.role_name || leave.user_email || '-'}</p></div>
                  <div><p className="text-sm font-medium text-slate-800">{leavePeriod(leave)}</p><p className="text-xs text-slate-500">{leave.total_days} calendar day{leave.total_days === 1 ? '' : 's'}</p></div>
                  <div><span className="inline-flex rounded-sm bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">{leave.leave_type}</span></div>
                  <div><p className="text-sm text-slate-700">{leave.reason}</p>{leave.notes && <p className="mt-1 text-xs text-slate-500">{leave.notes}</p>}<span className={`mt-1 inline-flex text-[10px] font-bold uppercase tracking-wider ${timing === 'current' ? 'text-amber-600' : timing === 'upcoming' ? 'text-blue-600' : 'text-green-600'}`}>{timing}</span></div>
                  <div className="flex justify-end gap-1">
                    {permissions.canEdit && <button type="button" disabled={leave.from_date < today} onClick={() => openEdit(leave)} data-testid={`edit-leave-${leave.id}`}
                      className="rounded p-2 text-slate-500 hover:bg-orange-50 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-30" aria-label={leave.from_date < today ? 'Past leave is read-only' : 'Edit leave'}><Pencil className="h-4 w-4" /></button>}
                    {permissions.canDelete && <button type="button" disabled={leave.from_date < today} onClick={() => setDeleteLeave(leave)} data-testid={`delete-leave-${leave.id}`}
                      className="rounded p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30" aria-label={leave.from_date < today ? 'Past leave is read-only' : 'Delete leave'}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <form onSubmit={saveLeave} className="my-8 w-full max-w-2xl rounded-sm bg-white" data-testid="leave-form">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div><h2 className="text-lg font-bold uppercase tracking-tight text-slate-900">{editingLeave ? 'Edit Leave' : 'Add Leave'}</h2><p className="text-xs text-slate-500">Attendance will be marked absent for every date in this period.</p></div>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            {error && <div className="mx-6 mt-5 flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 flex-shrink-0" />{error}</div>}
            <div className="grid gap-4 p-6 md:grid-cols-2">
              <label className="md:col-span-2"><span className="mb-1 block text-sm font-medium text-slate-700">Employee <span className="text-red-500">*</span></span>
                <select value={form.user_id} onChange={event => setForm(current => ({ ...current, user_id: event.target.value }))} required data-testid="leave-user"
                  className="w-full rounded-sm border border-slate-200 bg-white px-3 py-2.5 focus:border-orange-500 focus:outline-none">
                  <option value="">Select employee</option>
                  {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}{employee.role_name ? ` (${employee.role_name})` : ''}</option>)}
                </select>
              </label>
              <label><span className="mb-1 block text-sm font-medium text-slate-700">From Date <span className="text-red-500">*</span></span><input type="date" value={form.from_date} min={today} onChange={event => setForm(current => ({ ...current, from_date: event.target.value, to_date: current.to_date < event.target.value ? event.target.value : current.to_date }))} required data-testid="leave-from-date" className="w-full rounded-sm border border-slate-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" /></label>
              <label><span className="mb-1 block text-sm font-medium text-slate-700">To Date <span className="text-red-500">*</span></span><input type="date" value={form.to_date} min={form.from_date} onChange={event => setForm(current => ({ ...current, to_date: event.target.value }))} required data-testid="leave-to-date" className="w-full rounded-sm border border-slate-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" /></label>
              <label><span className="mb-1 block text-sm font-medium text-slate-700">Leave Type <span className="text-red-500">*</span></span><select value={form.leave_type} onChange={event => setForm(current => ({ ...current, leave_type: event.target.value }))} required data-testid="leave-type" className="w-full rounded-sm border border-slate-200 bg-white px-3 py-2.5 focus:border-orange-500 focus:outline-none">{LEAVE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}</select></label>
              <label><span className="mb-1 block text-sm font-medium text-slate-700">Reason <span className="text-red-500">*</span></span><input type="text" value={form.reason} onChange={event => setForm(current => ({ ...current, reason: event.target.value }))} required data-testid="leave-reason" placeholder="Reason for leave" className="w-full rounded-sm border border-slate-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" /></label>
              <label className="md:col-span-2"><span className="mb-1 block text-sm font-medium text-slate-700">Additional Notes</span><textarea value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} rows="3" placeholder="Optional notes" className="w-full resize-none rounded-sm border border-slate-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" /></label>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-sm border border-slate-200 px-5 py-2 text-slate-700 hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="save-leave" className="inline-flex items-center gap-2 rounded-sm bg-orange-500 px-6 py-2 font-bold uppercase tracking-wider text-white hover:bg-orange-600 disabled:opacity-50">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{editingLeave ? 'Update Leave' : 'Save Leave'}</button>
            </div>
          </form>
        </div>
      )}

      {deleteLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-sm bg-white p-6">
            <h3 className="text-lg font-bold text-slate-900">Delete Leave?</h3>
            <p className="mt-2 text-sm text-slate-600">This will remove the leave for <strong>{deleteLeave.user_name}</strong> and clear the attendance entries generated by it.</p>
            <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setDeleteLeave(null)} className="rounded-sm border border-slate-200 px-5 py-2 text-slate-700">Cancel</button><button type="button" onClick={confirmDelete} disabled={submitting} data-testid="confirm-delete-leave" className="inline-flex items-center gap-2 rounded-sm bg-red-600 px-5 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-50">{submitting && <Loader2 className="h-4 w-4 animate-spin" />}Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};
